import { DOCUMENT } from '@angular/common';
import { inject, Injectable, RendererFactory2 } from '@angular/core';
import {
  BehaviorSubject,
  catchError,
  filter,
  from,
  Observable,
  of,
  Subject,
  switchMap,
  take,
  throwError,
  timeout,
  TimeoutError,
} from 'rxjs';

import type { Environment } from '../../global';
import { EFeatureFlag, FeatureFlagService } from '../feature-flag.service';
import { RuntimeConfigService } from './runtime-config';

export enum RecaptchaAction {
  SUBMIT_PREQUAL = 'submit_prequal',
}

/**
 * Loads Google reCAPTCHA v3 and issues action-scoped tokens for form submit.
 * Honors the `enabled-recaptcha` LaunchDarkly flag; when disabled, `execute`
 * resolves with `null` so callers can skip the header.
 */
@Injectable({
  providedIn: 'root',
})
export class RecaptchaService {
  private readonly document = inject(DOCUMENT);
  private readonly featureFlagService = inject(FeatureFlagService);
  private readonly renderer = inject(RendererFactory2).createRenderer(
    null,
    null
  );
  // private readonly appConfigService: AppConfigService = inject(AppConfigService);
  private readonly prequalConfig: Environment =
    inject(RuntimeConfigService).prequalConfig;
  private scriptFailedToLoad = false;
  private readonly scriptLoadedSubject = new BehaviorSubject(false);
  private readonly recaptchaErrorSubject = new Subject<string>();

  readonly ready$ = this.scriptLoadedSubject
    .asObservable()
    .pipe(filter((isLoaded) => isLoaded));
  readonly recaptchaError$ = this.recaptchaErrorSubject.asObservable();

  loadRecaptchaScript(): void {
    if (this.document.getElementById('recaptcha-script')) {
      this.scriptLoadedSubject.next(true);
      return;
    }

    (window as any).recaptchaLoaded = () => this.scriptLoadedSubject.next(true);

    const script: HTMLScriptElement = this.renderer.createElement('script');
    this.renderer.setAttribute(script, 'id', 'recaptcha-script');
    this.renderer.setAttribute(
      script,
      'src',
      `https://www.google.com/recaptcha/api.js?render=${this.prequalConfig.RECAPTCHA_SITEKEY}&onload=recaptchaLoaded&trustedtypes=true`
    );
    this.renderer.setAttribute(script, 'async', 'true');
    this.renderer.setAttribute(script, 'defer', 'true');

    // Error callback in case the script fails to load
    script.onerror = (error) => {
      console.error(`Failed to load reCAPTCHA script due to ${error}`);
      this.scriptFailedToLoad = true;
    };
    this.renderer.appendChild(this.document.head, script);
  }

  execute(action: string): Observable<string | null> {
    return from(
      this.featureFlagService.isEnabled(EFeatureFlag.RECAPTCHA_ENABLED, true)
    ).pipe(
      switchMap((isEnabled) => {
        return isEnabled
          ? this.ready$.pipe(
              take(1),
              timeout(5000),
              switchMap(() =>
                from(
                  grecaptcha.execute(this.prequalConfig.RECAPTCHA_SITEKEY, {
                    action,
                  })
                )
              ),
              catchError((err) => {
                const errorMessage = 'reCAPTCHA execution failed';
                const error = err as Error;
                let failureReason = error.message;
                if (err instanceof TimeoutError) {
                  failureReason = this.scriptFailedToLoad
                    ? 'script failed to load'
                    : 'script failed to initialize properly';
                }

                console.error(
                  `reCAPTCHA execution failed due to ${failureReason}\n${error.stack}`
                );
                this.setRecaptchaError(errorMessage);
                return throwError(() => new Error(errorMessage));
              })
            )
          : of(null);
      })
    );
  }

  removeRecaptcha(): void {
    // Setting scriptLoadedSubject to false in order to prevent the execute() method from triggering a request to the
    // reCAPTCHA api before the script is reloaded properly if the page is navigated to again.
    this.scriptLoadedSubject.next(false);

    // Remove the reCAPTCHA badge element from the DOM
    const recaptchaBadge =
      this.document.getElementsByClassName('grecaptcha-badge')[0];
    if (recaptchaBadge) {
      this.renderer.removeChild(this.document.body, recaptchaBadge);
    }

    // Remove all scripts associated with reCAPTCHA
    Array.from(this.document.querySelectorAll('script[src]'))
      .filter(
        (element) =>
          (element as HTMLScriptElement).src.includes(
            'https://www.google.com/recaptcha'
          ) ||
          (element as HTMLScriptElement).src.includes(
            'https://www.gstatic.com/recaptcha'
          )
      )
      .forEach((script) => {
        this.renderer.removeChild(this.document.head, script);
      });
  }

  setRecaptchaError(errorMessage: string): void {
    this.recaptchaErrorSubject.next(errorMessage);
  }
}
